import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { v4 as uuid } from 'uuid';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { LoaderType } from '@fcom/common';
import { FinnairBoundItem } from '@fcom/dapi/api/models';
import { LocalDate } from '@fcom/core/utils';
import { DateSelection, Month } from '@fcom/ui-components/utils/date.interface';
import { CalendarNavigationType } from '@fcom/ui-components';

import { selectData, selectSearch, selectStatus } from '../../store/selectors';
import { SearchActions } from '../../store/actions';
import { TimetableFeatureState, TimetableStatus } from '../../interfaces/index';
import { getEarliestSearchDayInMonth, normalizeLocalDate } from '../../utils/timetable.utils';

@Component({
  selector: 'fin-timetable-calendar',
  templateUrl: './timetable-calendar.component.html',
  styleUrls: ['./timetable-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimetableCalendarComponent implements OnInit, OnDestroy {
  constructor(private store: Store<TimetableFeatureState>) {}

  selectedDays$: Observable<DateSelection>;
  status$: Observable<TimetableStatus>;
  results$: Observable<Record<string, FinnairBoundItem[]>>;
  identifier: string;
  minDate: LocalDate;
  maxDate: LocalDate;

  readonly CalendarNavigation = CalendarNavigationType;
  public readonly TimetableStatus = TimetableStatus;
  public readonly LoaderType = LoaderType;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  private ngDestroyed$ = new Subject();

  ngOnInit(): void {
    this.identifier = uuid();
    const today = LocalDate.now().toDate();
    const endDate = LocalDate.now().plusMonths(12).toDate();

    this.minDate = LocalDate.forDate(today.getFullYear(), today.getMonth() + 1, 1);

    this.maxDate = LocalDate.forDate(
      endDate.getFullYear(),
      endDate.getMonth() + 1,
      LocalDate.getAmountOfDaysInMonth(endDate.getFullYear(), endDate.getMonth() + 1)
    );

    this.selectedDays$ = this.store
      .pipe(selectSearch())
      .pipe(map((search) => ({ startDate: normalizeLocalDate(search.selectedDay) })));

    this.status$ = this.store.pipe(selectStatus()).pipe(map((status) => status.status));
    this.results$ = this.store.pipe(selectData()).pipe(map((data) => data.flights));
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }

  loadTimetables(): void {
    this.store.dispatch(SearchActions.setSearch({}));
  }

  onDatesSelected(dates: DateSelection): void {
    this.store.dispatch(
      SearchActions.setSearch({
        selectedDay: dates.startDate,
        selectedMonth: {
          year: dates.startDate.toDate().getFullYear(),
          month: dates.startDate.toDate().getMonth() + 1,
        },
      })
    );
  }

  monthChange(month: Month): void {
    this.store.dispatch(
      SearchActions.setSearch({
        selectedDay: getEarliestSearchDayInMonth({
          year: month.year,
          month: month.value,
        }),
        selectedMonth: {
          year: month.year,
          month: month.value,
        },
      })
    );
  }
}
